/**
 * Cinématique d'investiture — affichée uniquement au T0 d'une nouvelle
 * partie, jamais revue après (flag localStorage). Donne au joueur une
 * mise au monde narrative avant l'entrée dans le cockpit.
 *
 * Doctrine : « simple en surface » — pas de cockpit dense au boot, juste
 * trois phrases typographiques montées en pleine page. Vocabulaire
 * affiche politique (Grapus / Atelier populaire) cohérent avec
 * project_ui_cockpit_doctrine.
 *
 * 3 plans séquencés, puis bouton « Commencer le mandat ». Le joueur peut
 * skip instantanément en cliquant n'importe où à partir du plan 2.
 *
 * Contenu (2026-06-17) : pose le CONCEPT de la boucle de jeu — le temps file
 * au rythme du journal, vos décisions écrivent le pays — plutôt que de
 * répéter « vous êtes élu » (déjà dit par l'écran d'intro pré-sélection).
 */

import { useEffect, useState } from "react";

const INVESTITURE_SEEN_KEY = "mp_investiture_seen";

const PLANS = [
  {
    kicker: "PALAIS DE L'ÉLYSÉE",
    text: "Premier conseil des ministres dans une heure.",
  },
  { kicker: "UN MOIS PAR TOUR", text: "Le temps file au rythme du journal." },
  {
    kicker: "À VOUS DE DÉCIDER",
    text: "Mois après mois, le pays se transforme.",
  },
];

const PLAN_DURATION_MS = 1800;

interface Props {
  onComplete: () => void;
}

export function InvestitureOverlay({ onComplete }: Props) {
  const [planIdx, setPlanIdx] = useState(0);
  const [finished, setFinished] = useState(false);

  useEffect(() => {
    if (finished) return;
    if (planIdx >= PLANS.length) {
      setFinished(true);
      return;
    }
    const t = window.setTimeout(() => {
      setPlanIdx((i) => i + 1);
    }, PLAN_DURATION_MS);
    return () => window.clearTimeout(t);
  }, [planIdx, finished]);

  function handleSkip() {
    if (planIdx < 1) return; // empêche le clic trop précoce sur le 1er plan
    setFinished(true);
  }

  function handleStart() {
    try {
      localStorage.setItem(INVESTITURE_SEEN_KEY, "true");
    } catch {
      /* ignore */
    }
    onComplete();
  }

  // Garde élargie : `finished` est posé par useEffect au tick suivant
  // de planIdx, mais le rendu courant doit déjà afficher la vue finale
  // quand on a épuisé les plans, sinon `PLANS[planIdx]` est undefined
  // et crash sur `plan.kicker`.
  if (finished || planIdx >= PLANS.length) {
    return (
      <div className="investiture-backdrop" data-final>
        <div className="investiture-final">
          <div className="investiture-final-brand">MOI PRÉSIDENT</div>
          <p className="investiture-final-prompt">
            Le mandat commence maintenant.
          </p>
          <button
            type="button"
            className="investiture-final-btn"
            onClick={handleStart}
            autoFocus
          >
            Commencer le mandat →
          </button>
        </div>
      </div>
    );
  }

  const plan = PLANS[planIdx];

  return (
    <div
      className="investiture-backdrop"
      data-plan={planIdx}
      onClick={handleSkip}
      role="presentation"
    >
      <div className="investiture-plan">
        <div className="investiture-kicker">{plan.kicker}</div>
        <p className="investiture-text">{plan.text}</p>
      </div>
      {planIdx >= 1 && (
        <div className="investiture-skip-hint">cliquez pour passer</div>
      )}
    </div>
  );
}

/** Helper pour décider si l'investiture doit être montrée au boot. */
export function shouldShowInvestiture(): boolean {
  try {
    return localStorage.getItem(INVESTITURE_SEEN_KEY) !== "true";
  } catch {
    return true;
  }
}

/** Reset — appelé depuis "Nouvelle partie" pour rejouer l'investiture. */
export function resetInvestitureSeen(): void {
  try {
    localStorage.removeItem(INVESTITURE_SEEN_KEY);
  } catch {
    /* ignore */
  }
}
